/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { useSlidingStrip } from '@/shared/hooks/use-sliding-strip';
import type { PropertyMetrics } from './hooks/usePropertyMetrics';

interface PerformanceStripProps {
  metrics: PropertyMetrics | null;
  loading: boolean;
  error: string | null;
  /** Where each figure takes the broker; see the destinations below. */
  onOpenVisits: () => void;
  onOpenProposals: () => void;
  onOpenPublications: () => void;
  onShare: () => void;
}

interface PerformanceItem {
  id: string;
  label: string;
  value: string;
  unit?: string;
  go: () => void;
}

/**
 * How the listing is doing, at the top of the screen rather than the bottom
 * of the sidebar.
 *
 * These are the figures that decide whether the price comes down, so they sit
 * where the eye lands instead of below the fold. Each one is a button into
 * the place that explains it: visits into the visits tab, interested clients
 * into the offers they left, views and days on market into where the listing
 * is published, and shares into sharing it again.
 *
 * @param props - Metrics, load state and the navigation callbacks.
 * @returns The rendered strip.
 */
export const PerformanceStrip: React.FC<PerformanceStripProps> = ({
  metrics,
  loading,
  error,
  onOpenVisits,
  onOpenProposals,
  onOpenPublications,
  onShare,
}) => {
  const { t } = useTranslation('features/properties');
  const isMobile = useIsMobile();
  /* The row only slides where it is a row; on a wide screen it is a grid. */
  const strip = useSlidingStrip(isMobile);

  if (loading) {
    return (
      <div
        data-testid="property-performance-strip"
        className="grid grid-cols-[repeat(auto-fit,minmax(118px,1fr))] gap-[var(--space-3)] rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-card)] px-[var(--space-4)] py-[var(--space-3-5)]"
      >
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-[38px] animate-pulse rounded-(--radius-sm) bg-[var(--surface-tertiary)]" />
        ))}
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <div
        data-testid="property-performance-strip"
        className="flex items-center rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-card)] px-[var(--space-4)] py-[var(--space-3-5)]"
      >
        <Text className="m-0 text-[length:var(--text-body-sm)] text-[var(--text-tertiary)]">
          {error ?? t('detailView.metrics.unavailable')}
        </Text>
      </div>
    );
  }

  const items: PerformanceItem[] = [
    {
      id: 'views',
      label: t('detailView.metrics.views'),
      value: metrics.total_views.toLocaleString('pt-BR'),
      go: onOpenPublications,
    },
    {
      id: 'leads',
      label: t('detailView.metrics.interestedClients'),
      value: String(metrics.total_leads),
      go: onOpenProposals,
    },
    {
      id: 'visits',
      label: t('detailView.metrics.visits'),
      value: String(metrics.total_visits),
      go: onOpenVisits,
    },
    {
      id: 'shares',
      label: t('detailView.metrics.shares'),
      value: String(metrics.total_shares),
      go: onShare,
    },
    {
      id: 'days',
      label: t('detailView.metrics.timeOnMarket'),
      value: String(metrics.days_on_market),
      unit: t('detailView.metrics.days'),
      go: onOpenPublications,
    },
  ];

  return (
    /*
      A grid on a wide screen and a sliding strip on a narrow one. The strip
      fades whichever edge still has numbers behind it, so a row cut off at
      400px says so instead of looking like it simply ends there.
    */
    <div
      ref={strip.ref}
      {...strip.props}
      data-testid="property-performance-strip"
      className={`grid grid-cols-[repeat(auto-fit,minmax(118px,1fr))] gap-[var(--space-3)] rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-card)] px-[var(--space-4)] py-[var(--space-3-5)] max-md:flex ${strip.props.className}`}
    >
      {items.map((item) => (
        <UnstyledButton
          key={item.id}
          type="button"
          onClick={item.go}
          data-testid={`property-metric-${item.id}`}
          onClickCapture={(e) => { if (strip.wasDragged()) { e.preventDefault(); e.stopPropagation(); } }}
          className="flex min-w-0 flex-col items-start gap-[var(--space-0-5)] text-left max-md:min-w-[110px] max-md:snap-start"
        >
          <Text
            component="span"
            className="text-[length:var(--text-meta)] leading-[var(--leading-snug)] text-[var(--text-tertiary-soft)]"
          >
            {item.label}
          </Text>
          <Text component="span" className="flex items-baseline gap-[var(--space-1)]">
            <Text
              component="span"
              className="text-[length:var(--text-stat)] [font-weight:var(--font-bold)] leading-none tabular-nums text-[var(--seed-base)]"
            >
              {item.value}
            </Text>
            {item.unit && (
              <Text
                component="span"
                className="text-[length:var(--text-meta)] text-[var(--text-tertiary-soft)]"
              >
                {item.unit}
              </Text>
            )}
          </Text>
        </UnstyledButton>
      ))}
    </div>
  );
};
